import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { isWeb } from '@/utils/navigation';
import { SALES_PORTAL_PATH } from '@/experiences/sales-portal/sales-portal-route-config';
import { SALES_PORTAL_TAB_ROUTES } from '@/navigators/routes';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useBusinessOrganizationPricingConfig } from '@/experiences/sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useCallback } from 'react';
import { useIsValidPricingLayout } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-is-valid-pricing-layout';
import {
  USER_SETTINGS_KEY,
  UserSettingsAdapter,
} from '@/adapters/user-settings';
import { useSalesPortalNavigationStore } from '@/experiences/sales-portal/blocs/stores/sales-portal-navigation-store';

export const usePricingExperienceNavigation = () => {
  const { switchToExperience } = useExperienceStore();
  const {
    selectedRestaurantRelationship: { supplierUUID: selectedSupplierUUID },
    selectedRestaurantUUID,
    supplierEmployeeRole,
  } = useAuthenticatedUserContext();

  const isValidPricingLayout = useIsValidPricingLayout();
  const canRequestPricingAccess =
    isValidPricingLayout && Boolean(supplierEmployeeRole);

  const { hasPricingAccess } = useBusinessOrganizationPricingConfig({
    skip: !canRequestPricingAccess,
  });

  const canNavigate = canRequestPricingAccess && hasPricingAccess;

  const navigateToPricing = useCallback(async () => {
    if (!canNavigate) {
      return;
    }

    const adapter = new UserSettingsAdapter<{
      selectedRestaurantUUID: string;
      selectedSupplierUUID: string;
    }>(USER_SETTINGS_KEY);
    await adapter.save({
      selectedRestaurantUUID,
      selectedSupplierUUID,
    });

    // The Pricing Hub is the experience's landing screen, so entering from
    // outside opens it rather than one of its sections. Native has no URL to
    // seed — the persisted landing route below is what the stack reads.
    if (isWeb) {
      window.history.pushState({}, '', `${SALES_PORTAL_PATH}/pricing/overview`);
    }
    useSalesPortalNavigationStore
      .getState()
      .setLastActiveRoute(SALES_PORTAL_TAB_ROUTES.Pricing);
    switchToExperience(ExperienceMode.SALES_PORTAL);
  }, [
    canNavigate,
    selectedRestaurantUUID,
    selectedSupplierUUID,
    switchToExperience,
  ]);

  return { canNavigate, navigateToPricing };
};
